import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Package, Users, ShoppingBag, TrendingUp, Pencil, Check, X, Heart, ShoppingCart as CartIcon, Bell, UserPlus, Trash2, KeyRound } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import AppointmentsPanel from '@/components/admin/AppointmentsPanel';

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    images: [],
    category: '',
    fabric: '',
    colour: '',
    stock: '',
    tags: []
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [productInsights, setProductInsights] = useState([]);
  const [notifyRequests, setNotifyRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingCalls, setPendingCalls] = useState(0);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    // Wait for auth loading to complete before checking admin status
    if (loading) return;
    
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchAnalytics();
    fetchOrders();
    fetchProducts();
    fetchCustomers();
    fetchProductInsights();
    fetchNotifyRequests();
    fetchCategories();
    fetchFabrics();
    fetchTeamMembers();
    fetchPendingCalls();
  }, [user, loading]);

  const fetchPendingCalls = async () => {
    try {
      const res = await api.get('/appointments/admin/all', { params: { status: 'pending' } });
      setPendingCalls(res.data.pending_count);
    } catch (error) {
      console.error('Error fetching call bookings:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProductInsights = async () => {
    try {
      const response = await api.get('/admin/product-insights');
      setProductInsights(response.data);
    } catch (error) {
      console.error('Error fetching product insights:', error);
    }
  };

  const fetchNotifyRequests = async () => {
    try {
      const response = await api.get('/admin/notify-requests');
      setNotifyRequests(response.data);
    } catch (error) {
      console.error('Error fetching notify requests:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFabrics = async () => {
    try {
      const response = await api.get('/fabrics');
      setFabrics(response.data);
    } catch (error) {
      console.error('Error fetching fabrics:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/admin/team/members');
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!newMember.name || !newMember.email || !newMember.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (newMember.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post('/admin/team/members', newMember);
      toast.success(`Team member ${newMember.name} added. Share the login with them.`);
      setNewMember({ name: '', email: '', password: '' });
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleDeleteMember = async (uid) => {
    if (!window.confirm('Remove this team member? They will lose all access.')) return;
    try {
      await api.delete(`/admin/team/members/${uid}`);
      toast.success('Team member removed');
      fetchTeamMembers();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleResetMemberPassword = async (uid) => {
    const newPass = window.prompt('Enter new password (min 6 characters):');
    if (!newPass || newPass.length < 6) {
      if (newPass) toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/admin/team/members/${uid}/password`, { password: newPass });
      toast.success('Password reset. Share the new password with the member.');
    } catch {
      toast.error('Failed to reset password');
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.image || !newProduct.image.trim()) {
      toast.error('Please upload a main product image before creating the product.');
      return;
    }
    try {
      await api.post('/products', {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock),
        images: newProduct.images || [],
        tags: newProduct.tags || []
      });
      toast.success('Product created successfully!');
      setNewProduct({
        name: '',
        description: '',
        price: '',
        image: '',
        images: [],
        category: '',
        fabric: '',
        colour: '',
        stock: '',
        tags: []
      });
      fetchProducts();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.delete(`/products/${productId}`);
        toast.success('Product deleted');
        fetchProducts();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  const handleStartEdit = (product) => {
    setEditingProduct(product.id);
    setEditValues({
      name: product.name,
      description: product.description,
      stock: product.stock,
      price: product.price,
      image: product.image,
      images: product.images || [],
      fabric: product.fabric || '',
      category: product.category || '',
      tags: product.tags || []
    });
  };

  const handleSaveEdit = async (product) => {
    if (!editValues.image || !editValues.image.trim()) {
      toast.error('Main product image is required. Please upload an image.');
      return;
    }
    try {
      await api.put(`/products/${product.id}`, {
        ...product,
        name: editValues.name,
        description: editValues.description,
        stock: parseInt(editValues.stock),
        price: parseFloat(editValues.price),
        image: editValues.image,
        images: editValues.images || [],
        fabric: editValues.fabric || '',
        category: editValues.category || product.category,
        tags: editValues.tags || []
      });
      toast.success('Product updated!');
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setEditValues({});
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'shipped': return 'text-blue-600';
      case 'delivered': return 'text-green-700';
      case 'failed': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <div className="flex justify-between items-center mb-12">
        <h1 className="text-4xl md:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Admin Dashboard
        </h1>
        <Button onClick={() => navigate('/admin/cms')} variant="outline" className="rounded-none">
          Website Management
        </Button>
        <Button onClick={() => navigate('/admin/tools')} variant="outline" className="rounded-none ml-2" data-testid="admin-tools-btn">
          Store Tools
        </Button>
        <Button onClick={() => navigate('/admin/inventory')} variant="outline" className="rounded-none ml-2" data-testid="admin-inventory-btn">
          Inventory
        </Button>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-8 grid grid-cols-4 md:grid-cols-8 w-full max-w-5xl">
          <TabsTrigger value="overview" data-testid="admin-overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-orders-tab">Orders</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-products-tab">Products</TabsTrigger>
          <TabsTrigger value="calls" data-testid="admin-calls-tab">
            <span className="flex items-center gap-1.5">
              Calls
              {pendingCalls > 0 && (
                <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5" data-testid="pending-calls-badge">
                  {pendingCalls}
                </span>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="admin-insights-tab">Insights</TabsTrigger>
          <TabsTrigger value="waitlist" data-testid="admin-waitlist-tab">
            <span className="flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5" /> Waitlist
              {notifyRequests.reduce((s, r) => s + r.count, 0) > 0 && (
                <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 ml-1">
                  {notifyRequests.reduce((s, r) => s + r.count, 0)}
                </span>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-customers-tab">Customers</TabsTrigger>
          <TabsTrigger value="team" data-testid="admin-team-tab">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="calls" data-testid="admin-calls">
          <AppointmentsPanel />
        </TabsContent>

        <TabsContent value="overview" data-testid="admin-overview">
          {analytics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <div className="bg-secondary p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Orders</p>
                    <Package className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-3xl font-light" data-testid="total-orders">{analytics.total_orders}</p>
                </div>
                <div className="bg-secondary p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Products</p>
                    <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-3xl font-light" data-testid="total-products">{analytics.total_products}</p>
                </div>
                <div className="bg-secondary p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Customers</p>
                    <Users className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-3xl font-light" data-testid="total-customers">{analytics.total_customers}</p>
                </div>
                <div className="bg-secondary p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Revenue</p>
                    <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-3xl font-light" data-testid="total-revenue">₹{analytics.total_revenue?.toLocaleString('en-IN') || 0}</p>
                </div>
              </div>

              <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Recent Orders</h3>
              <div className="space-y-4">
                {analytics.recent_orders?.slice(0, 5).map((order) => (
                  <div key={order.id} className="border border-border p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-light">₹{order.total_amount?.toLocaleString('en-IN')}</p>
                      <span className={`text-xs uppercase tracking-widest ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="orders" className="space-y-6" data-testid="admin-orders-list">
          {orders.map((order) => (
            <div key={order.id} className="border border-border p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="font-medium">Order ID: {order.id}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString('en-IN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                <Select value={order.status} onValueChange={(status) => handleUpdateOrderStatus(order.id, status)}>
                  <SelectTrigger className="w-40" data-testid={`order-status-select-${order.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 mb-4">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.product_name} x {item.quantity}</span>
                    <span>₹{(item.price * item.quantity).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-4 flex justify-between">
                <span className="font-medium">Total</span>
                <span className="font-light">₹{order.total_amount?.toLocaleString('en-IN')}</span>
              </div>
              {order.special_request && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs uppercase tracking-widest text-amber-700 mb-1 font-medium">Special Request</p>
                  <p className="text-sm text-amber-900">{order.special_request}</p>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="products" data-testid="admin-products-section">
          <div className="mb-8 p-6 border border-border">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Add New Product</h3>
            <form onSubmit={handleCreateProduct} className="space-y-4" data-testid="add-product-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Product Name</Label>
                  <Input
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="rounded-none mt-2"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Category</Label>
                  <Select value={newProduct.category} onValueChange={(val) => setNewProduct({...newProduct, category: val})}>
                    <SelectTrigger className="rounded-none mt-2" data-testid="product-category-select">
                      <SelectValue placeholder="Select Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Fabric</Label>
                  <Select value={newProduct.fabric} onValueChange={(val) => setNewProduct({...newProduct, fabric: val})}>
                    <SelectTrigger className="rounded-none mt-2" data-testid="product-fabric-select">
                      <SelectValue placeholder="Select Fabric" />
                    </SelectTrigger>
                    <SelectContent>
                      {fabrics.map((fab) => (
                        <SelectItem key={fab.id} value={fab.name}>{fab.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Colour</Label>
                  <Input
                    value={newProduct.colour}
                    onChange={(e) => setNewProduct({...newProduct, colour: e.target.value})}
                    placeholder="e.g. Red, Indigo Blue, Multi"
                    className="rounded-none mt-2"
                    data-testid="product-colour-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest mb-2 block">Tags</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(newProduct.tags || []).includes('best_seller')}
                        onChange={(e) => {
                          const tags = newProduct.tags || [];
                          setNewProduct({...newProduct, tags: e.target.checked ? [...tags, 'best_seller'] : tags.filter(t => t !== 'best_seller')});
                        }}
                      />
                      <span className="text-sm">Best Seller</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(newProduct.tags || []).includes('designers_choice')}
                        onChange={(e) => {
                          const tags = newProduct.tags || [];
                          setNewProduct({...newProduct, tags: e.target.checked ? [...tags, 'designers_choice'] : tags.filter(t => t !== 'designers_choice')});
                        }}
                      />
                      <span className="text-sm">Designer's Choice</span>
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Description</Label>
                <Textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  className="rounded-none mt-2"
                  rows={3}
                  required
                  data-testid="product-description-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Price (₹)</Label>
                  <Input
                    type="number"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                    className="rounded-none mt-2"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Stock</Label>
                  <Input
                    type="number"
                    value={newProduct.stock}
                    onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
                    className="rounded-none mt-2"
                    required
                    data-testid="product-stock-input"
                  />
                </div>
              </div>
              <ImageUpload
                label="Main Product Image"
                value={newProduct.image}
                onChange={(url) => setNewProduct({...newProduct, image: url})}
              />
              {/* Additional Images */}
              <div>
                <Label className="text-xs uppercase tracking-widest mb-2 block">Additional Images ({(newProduct.images || []).length})</Label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {(newProduct.images || []).map((img, idx) => (
                    <div key={idx} className="relative w-24 h-24">
                      <img src={img} alt={`Additional ${idx + 1}`} className="w-full h-full object-cover rounded-lg border border-border" />
                      <button
                        type="button"
                        onClick={() => setNewProduct({...newProduct, images: newProduct.images.filter((_, i) => i !== idx)})}
                        className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >x</button>
                    </div>
                  ))}
                </div>
                <ImageUpload
                  label=""
                  value=""
                  onChange={(url) => {
                    if (url) setNewProduct({...newProduct, images: [...(newProduct.images || []), url]});
                  }}
                />
              </div>
              <Button type="submit" className="rounded-none" data-testid="create-product-button">
                Create Product
              </Button>
            </form>
          </div>

          <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>All Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-list">
            {products.map((product) => (
              <div key={product.id} className="border border-border p-4">
                {editingProduct === product.id ? (
                  <>
                    <ImageUpload
                      label="Main Product Image"
                      value={editValues.image}
                      onChange={(url) => setEditValues({...editValues, image: url})}
                    />
                    {/* Additional Images in Edit */}
                    <div className="mt-3">
                      <Label className="text-xs uppercase tracking-widest mb-2 block">Additional Images ({(editValues.images || []).length})</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(editValues.images || []).map((img, idx) => (
                          <div key={idx} className="relative w-16 h-16">
                            <img src={img} alt={`Extra ${idx + 1}`} className="w-full h-full object-cover rounded-lg border border-border" />
                            <button
                              type="button"
                              onClick={() => setEditValues({...editValues, images: editValues.images.filter((_, i) => i !== idx)})}
                              className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                            >x</button>
                          </div>
                        ))}
                      </div>
                      <ImageUpload
                        label=""
                        value=""
                        onChange={(url) => {
                          if (url) setEditValues({...editValues, images: [...(editValues.images || []), url]});
                        }}
                      />
                    </div>
                    {/* Editable Name */}
                    <div className="mt-4 mb-3">
                      <Label className="text-xs uppercase tracking-widest">Name</Label>
                      <Input
                        value={editValues.name}
                        onChange={(e) => setEditValues({...editValues, name: e.target.value})}
                        className="rounded-none mt-1"
                      />
                    </div>
                    <div className="mb-3">
                      <Label className="text-xs uppercase tracking-widest">Description</Label>
                      <Textarea
                        value={editValues.description}
                        onChange={(e) => setEditValues({...editValues, description: e.target.value})}
                        className="rounded-none mt-1"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <Label className="text-xs uppercase tracking-widest">Category</Label>
                        <Select value={editValues.category} onValueChange={(val) => setEditValues({...editValues, category: val})}>
                          <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest">Fabric</Label>
                        <Select value={editValues.fabric || ''} onValueChange={(val) => setEditValues({...editValues, fabric: val})}>
                          <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select Fabric" /></SelectTrigger>
                          <SelectContent>
                            {fabrics.map((fab) => (
                              <SelectItem key={fab.id} value={fab.name}>{fab.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <Label className="text-xs uppercase tracking-widest">Price (₹)</Label>
                        <Input type="number" value={editValues.price} onChange={(e) => setEditValues({...editValues, price: e.target.value})} className="rounded-none mt-1" data-testid={`edit-price-${product.id}`} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest">Stock</Label>
                        <Input type="number" value={editValues.stock} onChange={(e) => setEditValues({...editValues, stock: e.target.value})} className="rounded-none mt-1" data-testid={`edit-stock-${product.id}`} />
                      </div>
                    </div>
                    <div className="flex gap-4 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={(editValues.tags || []).includes('best_seller')} onChange={(e) => {
                          const tags = editValues.tags || [];
                          setEditValues({...editValues, tags: e.target.checked ? [...tags, 'best_seller'] : tags.filter(t => t !== 'best_seller')});
                        }} />
                        <span className="text-xs">Best Seller</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={(editValues.tags || []).includes('designers_choice')} onChange={(e) => {
                          const tags = editValues.tags || [];
                          setEditValues({...editValues, tags: e.target.checked ? [...tags, 'designers_choice'] : tags.filter(t => t !== 'designers_choice')});
                        }} />
                        <span className="text-xs">Designer's Choice</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="rounded-none flex-1"
                        onClick={() => handleSaveEdit(product)}
                        data-testid={`save-edit-${product.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" /> Save
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm" 
                        className="rounded-none flex-1"
                        onClick={handleCancelEdit}
                        data-testid={`cancel-edit-${product.id}`}
                      >
                        <X className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <img src={product.image} alt={product.name} className="w-full h-48 object-cover mb-4 rounded-lg" />
                    <h4 className="font-medium mb-2">{product.name}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{product.category}</p>
                    <p className="text-lg font-light mb-1">₹{product.price?.toLocaleString('en-IN')}</p>
                    <p className="text-sm text-muted-foreground mb-1">Stock: {product.stock}</p>
                    {product.images?.length > 0 && (
                      <p className="text-xs text-muted-foreground mb-4">{product.images.length + 1} images</p>
                    )}
                    {(!product.images || product.images.length === 0) && <div className="mb-4" />}
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        size="sm" 
                        className="rounded-none flex-1"
                        onClick={() => handleStartEdit(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="rounded-none flex-1"
                        onClick={() => handleDeleteProduct(product.id)}
                        data-testid={`delete-product-${product.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Product Insights Tab */}
        <TabsContent value="insights" data-testid="admin-insights">
          <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Product Insights
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            See which products are most wishlisted and added to carts by customers.
          </p>
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary">
                  <th className="text-left p-4 text-xs uppercase tracking-widest font-medium text-muted-foreground">Product</th>
                  <th className="text-center p-4 text-xs uppercase tracking-widest font-medium text-muted-foreground">
                    <div className="flex items-center justify-center gap-1">
                      <Heart className="h-4 w-4 text-red-500" /> Wishlisted
                    </div>
                  </th>
                  <th className="text-center p-4 text-xs uppercase tracking-widest font-medium text-muted-foreground">
                    <div className="flex items-center justify-center gap-1">
                      <CartIcon className="h-4 w-4" /> In Carts
                    </div>
                  </th>
                  <th className="text-center p-4 text-xs uppercase tracking-widest font-medium text-muted-foreground">Total Interest</th>
                </tr>
              </thead>
              <tbody>
                {productInsights.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-muted-foreground">No data yet. Insights will appear as customers interact with products.</td>
                  </tr>
                ) : (
                  productInsights.map((item, idx) => (
                    <tr key={item.product_id} className={`border-t border-border ${idx === 0 && item.wishlist_count + item.cart_count > 0 ? 'bg-amber-50' : ''}`} data-testid={`insight-row-${item.product_id}`}>
                      <td className="p-4">
                        <span className="font-medium">{item.name}</span>
                        {idx === 0 && item.wishlist_count + item.cart_count > 0 && (
                          <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Most Popular</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <span className={`text-lg font-light ${item.wishlist_count > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                          {item.wishlist_count}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`text-lg font-light ${item.cart_count > 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                          {item.cart_count}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="text-lg font-medium">{item.wishlist_count + item.cart_count}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="waitlist" data-testid="admin-waitlist">
          <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Back-in-Stock Waitlist
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Customers who want to be notified when out-of-stock products return. Reach out to them when you restock.
          </p>
          {notifyRequests.length === 0 ? (
            <div className="border border-border rounded-xl p-12 text-center">
              <Bell className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No waitlist requests yet.</p>
              <p className="text-xs text-muted-foreground mt-1">When a product goes out of stock and customers subscribe, they&apos;ll appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifyRequests.map((req) => (
                <div key={req.product_id} className="border border-border rounded-xl p-5" data-testid={`waitlist-row-${req.product_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="font-medium">{req.product_name}</p>
                      <p className="text-xs text-muted-foreground">Product ID: {req.product_id.slice(0, 8)}…</p>
                    </div>
                    <span className="bg-red-500 text-white text-xs uppercase tracking-widest px-3 py-1 rounded-full">
                      {req.count} waiting
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {req.emails.map((email, idx) => (
                      <a
                        key={idx}
                        href={`mailto:${email}?subject=${encodeURIComponent(req.product_name + ' is back in stock!')}&body=${encodeURIComponent('Hi,\n\nGreat news — the ' + req.product_name + ' you were interested in is back in stock at Bandhani Heritage.\n\nOrder yours here before it sells out again.\n\nWarm regards,\nBandhani Heritage')}`}
                        className="text-xs bg-secondary hover:bg-primary/10 hover:text-primary transition-colors px-3 py-1.5 rounded-full border border-border"
                        data-testid={`waitlist-email-${idx}`}
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Click any email to compose a back-in-stock message.
                  </p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="customers" data-testid="admin-customers-list">
          <div className="space-y-4">
            {customers.map((customer) => {
              const cid = customer.user_id || customer.id;
              return (
              <div
                key={cid}
                className="border border-border p-6 rounded-xl hover:border-primary/40 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/admin/customers/${cid}`)}
                data-testid={`customer-row-${cid}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-medium text-lg">{customer.name}</p>
                    <p className="text-sm text-muted-foreground">{customer.email}</p>
                    {customer.picture && (
                      <img src={customer.picture} alt={customer.name} className="w-10 h-10 rounded-full mt-2" />
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      Joined: {new Date(customer.created_at).toLocaleDateString('en-IN', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      ID: {(customer.id || customer.user_id || '').slice(0, 8)}...
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border">
                  <div className="bg-secondary/50 p-3 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Orders</p>
                    <p className="text-2xl font-light">
                      {orders.filter(o => o.user_id === (customer.id || customer.user_id)).length}
                    </p>
                  </div>
                  <div className="bg-secondary/50 p-3 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Spent</p>
                    <p className="text-2xl font-light">
                      ₹{orders
                        .filter(o => o.user_id === (customer.id || customer.user_id) && o.status === 'confirmed')
                        .reduce((sum, o) => sum + (o.total_amount || 0), 0)
                        .toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                <p className="text-xs uppercase tracking-widest text-primary mt-3 text-right">View Full Detail →</p>
              </div>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="team" data-testid="admin-team">
          <div className="mb-8">
            <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Team Members
            </h3>
            <p className="text-sm text-muted-foreground">
              Create logins for people who help manage inventory. Members can add/edit/delete inventory items and images, but cannot see orders, customers, or CMS.
            </p>
          </div>

          <form onSubmit={handleCreateMember} className="border border-border rounded-xl p-5 mb-8 max-w-2xl" data-testid="add-member-form">
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Add New Member
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Name</Label>
                <Input
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  className="rounded-full mt-1"
                  placeholder="Full name"
                  data-testid="member-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  className="rounded-full mt-1"
                  placeholder="member@example.com"
                  data-testid="member-email-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Password</Label>
                <Input
                  type="text"
                  value={newMember.password}
                  onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                  className="rounded-full mt-1"
                  placeholder="Min 6 characters"
                  data-testid="member-password-input"
                />
              </div>
            </div>
            <Button type="submit" className="rounded-full mt-4" data-testid="add-member-btn">
              <UserPlus className="h-4 w-4 mr-2" /> Create Member
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              The member will log in via the regular login page. Share the email + password with them.
            </p>
          </form>

          {teamMembers.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl p-12 text-center">
              <Users className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No team members yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="team-members-list">
              {teamMembers.map((m) => {
                const mid = m.user_id || m.id;
                return (
                  <div key={mid} className="border border-border rounded-xl p-5" data-testid={`member-row-${mid}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium">{m.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{m.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Added {new Date(m.created_at).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full whitespace-nowrap">
                        Inventory Access
                      </span>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline" size="sm" className="rounded-full flex-1"
                        onClick={() => handleResetMemberPassword(mid)}
                        data-testid={`reset-password-${mid}`}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset Password
                      </Button>
                      <Button
                        variant="destructive" size="sm" className="rounded-full"
                        onClick={() => handleDeleteMember(mid)}
                        data-testid={`delete-member-${mid}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;